import { Component } from '@theme/component';

/**
 * @typedef {Object} StoreSwitcherRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement | HTMLDialogElement} panel
 */

/**
 * @extends {Component<StoreSwitcherRefs>}
 */
class StoreSwitcher extends Component {
  requiredRefs = ['trigger', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#handleOutsideClick);
    this.addEventListener('keydown', this.#handleKeydown);

    if (this.refs.panel instanceof HTMLDialogElement) {
      this.refs.panel.addEventListener('close', this.#handleDialogClose);
      this.refs.panel.addEventListener('click', this.#handleDialogClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#handleOutsideClick);
    this.removeEventListener('keydown', this.#handleKeydown);

    if (this.refs.panel instanceof HTMLDialogElement) {
      this.refs.panel.removeEventListener('close', this.#handleDialogClose);
      this.refs.panel.removeEventListener('click', this.#handleDialogClick);
    }
  }

  /**
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();

    if (this.refs.panel instanceof HTMLDialogElement) {
      this.refs.panel.showModal();
      this.refs.trigger.setAttribute('aria-expanded', 'true');
      return;
    }

    const isOpen = !this.refs.panel.hidden;
    this.#setDesktopOpen(!isOpen);
  }

  /**
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();

    if (this.refs.panel instanceof HTMLDialogElement) {
      this.refs.panel.close();
      this.refs.trigger.setAttribute('aria-expanded', 'false');
      this.refs.trigger.focus();
      return;
    }

    this.#setDesktopOpen(false);
    this.refs.trigger.focus();
  }

  /**
   * @param {boolean} isOpen
   */
  #setDesktopOpen(isOpen) {
    this.refs.panel.hidden = !isOpen;
    this.refs.trigger.setAttribute('aria-expanded', String(isOpen));
  }

  /**
   * @param {PointerEvent} event
   */
  #handleOutsideClick = (event) => {
    if (this.refs.panel instanceof HTMLDialogElement) return;
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#setDesktopOpen(false);
  };

  #handleDialogClose = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (event.target !== this.refs.panel) return;
    this.close(event);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.refs.panel instanceof HTMLDialogElement && !this.refs.panel.open) return;
    if (!(this.refs.panel instanceof HTMLDialogElement) && this.refs.panel.hidden) return;
    this.close(event);
  };
}

if (!customElements.get('store-switcher')) {
  customElements.define('store-switcher', StoreSwitcher);
}
